import React, { useCallback } from 'react';
import { 
  Box, 
  IconButton, 
  Select, 
  MenuItem, 
  Typography, 
  FormControl,
  InputLabel,
  Button,
  ButtonGroup
} from '@mui/material';
import {
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
  FastForward as FastForwardIcon,
  FastRewind as FastRewindIcon,
  PlayArrow as PlayArrowIcon,
  Forward5 as Forward5Icon,
  Replay5 as Replay5Icon
} from '@mui/icons-material';

/**
 * Navigation controls for lap and segment navigation
 * Emits navigation events without handling video logic directly
 */
function NavigationControls({
  lapData = [],
  segments = [],
  currentLap = 0,
  currentSegment = 1,
  skipSeconds = 5,
  onNavigateToLap,
  onNavigateToSegment,
  onSkipSeconds,
  onSkipLap,
  onSetSkipSeconds,
  showLapControls = true,
  showSegmentControls = false,
  showSkipControls = true,
  compact = false,
  className = ''
}) {

  // Format lap time for display
  const formatLapTime = useCallback((duration) => {
    if (!duration || isNaN(duration)) return '--:--';
    
    const minutes = Math.floor(duration / 60);
    const seconds = (duration % 60).toFixed(3);
    return `${minutes}:${seconds.padStart(6, '0')}`;
  }, []);

  // Get lap display name
  const getLapDisplayName = useCallback((lap) => {
    // Use pre-defined name if available (for synthetic laps)
    if (lap.name) return lap.name;
    
    // Handle synthetic laps by number and flags
    if (lap.number === 0 || lap.synthetic) {
      return lap.name || 'Out Lap';  
    }
    
    // Check if this is actually a synthetic "in lap" by looking for special markers
    const isActualInLap = lap.isInLap === true;
    if (isActualInLap) return 'In Lap';
    
    // For real race data, just use the lap number
    return `Lap ${lap.number}`;
  }, [lapData]);

  // Handle lap navigation - always trigger navigation even for current lap
  const handleLapChange = useCallback((event) => {
    const lapNumber = parseInt(event.target.value);
    // Always trigger navigation to allow "go back to start of current lap"
    onNavigateToLap?.(lapNumber);
  }, [onNavigateToLap]);

  const handlePreviousLap = useCallback(() => {
    const currentIndex = lapData.findIndex(lap => lap.number === currentLap);
    if (currentIndex > 0) {
      onSkipLap?.(-1);
    }
  }, [lapData, currentLap, onSkipLap]);

  const handleNextLap = useCallback(() => {
    const currentIndex = lapData.findIndex(lap => lap.number === currentLap);
    if (currentIndex < lapData.length - 1) {
      onSkipLap?.(1);
    }
  }, [lapData, currentLap, onSkipLap]);

  // Handle segment navigation
  const handleSegmentChange = useCallback((event) => {
    const segmentNumber = parseInt(event.target.value);
    onNavigateToSegment?.(segmentNumber);
  }, [onNavigateToSegment]);

  const handlePreviousSegment = useCallback(() => {
    if (!segments || segments.length === 0) return;
    const newSegment = currentSegment > 1 ? currentSegment - 1 : segments.length;
    onNavigateToSegment?.(newSegment);
  }, [currentSegment, segments, onNavigateToSegment]);

  const handleNextSegment = useCallback(() => {
    if (!segments || segments.length === 0) return;
    const newSegment = currentSegment < segments.length ? currentSegment + 1 : 1;
    onNavigateToSegment?.(newSegment);
  }, [currentSegment, segments, onNavigateToSegment]);

  // Handle skip controls
  const handleSkipBackward = useCallback(() => {
    onSkipSeconds?.(-skipSeconds);
  }, [skipSeconds, onSkipSeconds]);

  const handleSkipForward = useCallback(() => {
    onSkipSeconds?.(skipSeconds);
  }, [skipSeconds, onSkipSeconds]);

  const handleSkipSecondsChange = useCallback((event) => {
    onSetSkipSeconds?.(parseInt(event.target.value));
  }, [onSetSkipSeconds]);

  if (compact) {
    return (
      <Box className={className} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {/* Compact Lap Selector */}
        {showLapControls && lapData.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={currentLap}
              onChange={handleLapChange}
              displayEmpty
            >
              {lapData.map((lap) => (
                <MenuItem key={lap.number} value={lap.number}>
                  {getLapDisplayName(lap)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Skip Controls */}
        {showSkipControls && (
          <ButtonGroup size="small">
            <IconButton onClick={handleSkipBackward} size="small">
              <Replay5Icon />
            </IconButton>
            <IconButton onClick={handleSkipForward} size="small">
              <Forward5Icon />
            </IconButton>
          </ButtonGroup>
        )}
      </Box>
    );
  }

  return (
    <Box className={className} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Lap Controls */}
      {showLapControls && lapData.length > 0 && (
        <Box sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{ 
            p: 0.5, 
            backgroundColor: 'grey.50', 
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Lap Navigation
            </Typography>
            
            {/* Quick jump to first real lap (lap 1) */}
            {lapData.find(lap => lap.number === 1) && (
              <Button 
                size="small" 
                variant="text"
                onClick={() => onNavigateToLap?.(1)}
                sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
              >
                Race Start
              </Button>
            )}
          </Box>

          {/* Controls */}
          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={handlePreviousLap}
              size="small"
              disabled={currentLap === lapData[0]?.number}
            >
              <SkipPreviousIcon />
            </IconButton>

            <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
              <Select
                value={currentLap}
                onChange={handleLapChange}
                displayEmpty
              >
                {lapData.map((lap) => (
                  <MenuItem key={lap.number} value={lap.number}>
                    {getLapDisplayName(lap)} - {formatLapTime(lap.duration)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton 
              onClick={handleNextLap}
              size="small"
              disabled={currentLap === lapData[lapData.length - 1]?.number}
            >
              <SkipNextIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Segment Controls */}
      {showSegmentControls && segments && segments.length > 0 && (
        <Box sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{ 
            p: 0.5, 
            backgroundColor: 'grey.50', 
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Sector Navigation
            </Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={handlePreviousSegment}
              size="small"
            >
              <FastRewindIcon />
            </IconButton>

            <FormControl size="small" sx={{ flex: 1, minWidth: 80 }}>
              <Select
                value={currentSegment}
                onChange={handleSegmentChange}
                displayEmpty
              >
                {segments.map((segment) => (
                  <MenuItem key={segment.number} value={segment.number}>
                    Sector {segment.number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton 
              onClick={handleNextSegment}
              size="small"
            >
              <FastForwardIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Skip Controls */}
      {showSkipControls && (
        <Box sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{ 
            p: 0.5, 
            backgroundColor: 'grey.50', 
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Time Skip
            </Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={handleSkipBackward}
              size="small"
              title={`Skip back ${skipSeconds}s`}
            >
              <SkipPreviousIcon />
            </IconButton>

            <FormControl size="small" sx={{ flex: 1, minWidth: 60 }}>
              <Select
                value={skipSeconds}
                onChange={handleSkipSecondsChange}
                displayEmpty
              >
                <MenuItem value={1}>1s</MenuItem>
                <MenuItem value={2}>2s</MenuItem>
                <MenuItem value={3}>3s</MenuItem>
                <MenuItem value={5}>5s</MenuItem>
                <MenuItem value={10}>10s</MenuItem>
                <MenuItem value={15}>15s</MenuItem>
                <MenuItem value={30}>30s</MenuItem>
              </Select>
            </FormControl>

            <IconButton 
              onClick={handleSkipForward}
              size="small"
              title={`Skip forward ${skipSeconds}s`}
            >
              <SkipNextIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Current Position Display */}
      <Box sx={{ 
        p: 1, 
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: 1,
        textAlign: 'center'
      }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {showLapControls && lapData.length > 0 && (
            <>Lap {currentLap}</>
          )}
          {showSegmentControls && segments && segments.length > 0 && (
            <>{showLapControls ? ' • ' : ''}Sector {currentSegment}</>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

export default NavigationControls; 